import { DropdownOption, QuestRoutine } from "@joonapp/web-shared"
import dayjs from "dayjs"
import { useMemo } from "react"
import { useTranslation } from "react-i18next"
import { useShallow } from "zustand/react/shallow"

import { useSpecifyDateModalStore } from "./SpecifyDateModal"
import { StartDateOption } from "../../../../constants"
import { ROUTINE_LABEL_KEYS } from "../questLabelKeys"
import { useAddEditQuestStore } from "../useAddEditQuestStore"

export const useDateTime = () => {
  const {
    startDate,
    isOngoing,
    routine,
    setStartDate,
    setRoutine,
    setReminderTimeMap,
  } = useAddEditQuestStore(
    useShallow((state) => ({
      startDate: state.startDate,
      isOngoing: state.isOngoing,
      routine: state.routine,
      setStartDate: state.setStartDate,
      setRoutine: state.setRoutine,
      setReminderTimeMap: state.setReminderTimeMap,
    }))
  )
  const { onOpen: onOpenSpecifyDateModal } = useSpecifyDateModalStore()
  const { t } = useTranslation()

  const selectedStartDate = useMemo(() => {
    if (startDate && dayjs(startDate).isSame(dayjs(), "day"))
      return StartDateOption.TODAY
    if (startDate && dayjs(startDate).isSame(dayjs().add(1, "day"), "day"))
      return StartDateOption.TOMORROW
    return StartDateOption.CUSTOM
  }, [startDate])

  const startDateOptions: DropdownOption[] = [
    { label: t("QUEST_TODAY"), value: StartDateOption.TODAY },
    { label: t("QUEST_TOMORROW"), value: StartDateOption.TOMORROW },
    {
      label:
        selectedStartDate === StartDateOption.CUSTOM
          ? dayjs(startDate).format("MMM DD, YYYY")
          : t("QUEST_SPECIFY_DATE_OPTION"),
      value: StartDateOption.CUSTOM,
    },
  ]

  const getRoutineString = () => {
    if (isOngoing) return t("ROUTINE_ANYTIME")
    return t(ROUTINE_LABEL_KEYS[routine])
  }

  const onRoutineClick = (
    e: React.MouseEvent<HTMLButtonElement>,
    questRoutine: QuestRoutine
  ) => {
    e.stopPropagation()
    if (routine === questRoutine) return
    setRoutine(questRoutine)
    setReminderTimeMap({
      [questRoutine]: "",
    })
  }

  const setSelectedStartDate = (value: StartDateOption) => {
    switch (value) {
      case StartDateOption.TODAY:
        setStartDate(dayjs().toString())
        break
      case StartDateOption.TOMORROW:
        setStartDate(dayjs().add(1, "day").toString())
        break
      case StartDateOption.CUSTOM:
        onOpenSpecifyDateModal()
        break
    }
  }

  return {
    selectedStartDate,
    startDateOptions,
    getRoutineString,
    setSelectedStartDate,
    onRoutineClick,
  }
}
